import * as THREE from "three";

/**
 * Procedural grayscale surface textures, generated once on a canvas and cached.
 * They're used as material `.map`s: MeshStandard/MeshToon multiply map.rgb ×
 * material.color, so a mostly-light grayscale texture tints to the material's
 * colour while adding fur strands / reptile scales / hide mottle. This lets the
 * mounts keep their per-rider paint AND read as textured hide.
 */

let furTex: THREE.CanvasTexture | null = null;
let hideTex: THREE.CanvasTexture | null = null;
let scaleTex: THREE.CanvasTexture | null = null;

function canvas(size = 128): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return [c, c.getContext("2d")!];
}

function finish(c: HTMLCanvasElement, repeat = 3): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeat, repeat);
  t.anisotropy = 4;
  t.userData.shared = true; // module-cached — teardown helpers must not dispose it
  return t;
}

/** Short-hair fur: fine directional strands over a near-white base. */
export function furTexture(): THREE.CanvasTexture {
  if (furTex) return furTex;
  const [c, g] = canvas(128);
  g.fillStyle = "#cfcfcf";
  g.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 2600; i++) {
    const x = Math.random() * 128, y = Math.random() * 128;
    const len = 3 + Math.random() * 5;
    const ang = -1.15 + (Math.random() - 0.5) * 0.5; // roughly combed one way
    const light = Math.random() > 0.5;
    g.strokeStyle = light ? "rgba(255,255,255,0.5)" : "rgba(70,60,50,0.4)";
    g.lineWidth = 0.8;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
    g.stroke();
  }
  furTex = finish(c, 4);
  return furTex;
}

/** Coarser mottled hide for camels — soft blotches, no strong combing. */
export function hideTexture(): THREE.CanvasTexture {
  if (hideTex) return hideTex;
  const [c, g] = canvas(128);
  g.fillStyle = "#d0cbc2";
  g.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * 128, y = Math.random() * 128;
    const r = 6 + Math.random() * 16;
    g.fillStyle = Math.random() > 0.5 ? "rgba(150,135,110,0.18)" : "rgba(255,250,240,0.22)";
    g.beginPath();
    g.ellipse(x, y, r, r * (0.6 + Math.random() * 0.5), Math.random() * Math.PI, 0, Math.PI * 2);
    g.fill();
  }
  hideTex = finish(c, 3);
  return hideTex;
}

/** Overlapping reptile scales in rows — darker rims, lit centres. */
export function scaleTexture(): THREE.CanvasTexture {
  if (scaleTex) return scaleTex;
  const [c, g] = canvas(128);
  g.fillStyle = "#c4c4c4";
  g.fillRect(0, 0, 128, 128);
  const step = 15;
  for (let row = 0; row * step * 0.72 < 128 + step; row++) {
    const y = row * step * 0.72;
    const off = (row % 2) * (step / 2);
    for (let col = -1; col * step < 128 + step; col++) {
      const x = col * step + off;
      // scale body: radial gradient — pale centre, shaded lower rim
      const grad = g.createRadialGradient(x, y - 2, 1, x, y + 3, step * 0.75);
      grad.addColorStop(0, "#eaeaea");
      grad.addColorStop(0.7, "#c0c0c0");
      grad.addColorStop(1, "#8f8f8f");
      g.fillStyle = grad;
      g.beginPath();
      g.arc(x, y, step * 0.62, Math.PI, 0); // top half-round
      g.arc(x, y + step * 0.28, step * 0.62, 0, Math.PI * 0.5, false);
      g.closePath();
      g.fill();
      g.strokeStyle = "rgba(60,60,60,0.35)";
      g.lineWidth = 0.8;
      g.beginPath();
      g.arc(x, y, step * 0.62, Math.PI, 0);
      g.stroke();
    }
  }
  scaleTex = finish(c, 5);
  return scaleTex;
}
